import type { SxProps } from '@mui/material';
import { Typography } from '@mui/material';
import React from 'react';
import { ContactInfo, ContactInput } from 'sr-types/lib/person/v1/graphql';
import { getContactInfoForPreview } from '../../../person/detailsPage/detailsPageUtilities/personBusinessContacts';
import { SIZES } from '../../constants/constants';
import { H } from '../../Layout';
import PreviewContact from './PreviewContact';

type PreviewContactInfoProps = {
    contactInfo?: ContactInfo;
    businessContacts: ContactInput[];
    organizationName?: string;
    previewContactIconSx?: SxProps;
};

const PREVIEW_CONTACT_CONTAINER_SX = { width: 'fit-content' };
const PREVIEW_CONTACT_INFO_SX = { fontSize: '12px' };

export default function PreviewContactInfo({
    contactInfo,
    businessContacts,
    organizationName,
    previewContactIconSx
}: PreviewContactInfoProps) {
    const { phoneNumber, emailAddress, phoneIcon, emailIcon } = getContactInfoForPreview(contactInfo, businessContacts);
    const trimmedOrganizationName = organizationName?.trim();
    const hasPreviewContact = Boolean(emailAddress || phoneNumber);

    const previewContacts = (
        <>
            {emailAddress && (
                <PreviewContact
                    href={`mailto:${emailAddress}`}
                    icon={emailIcon}
                    hoverTitle={emailAddress}
                    clipboardContent={emailAddress}
                    containerSx={PREVIEW_CONTACT_CONTAINER_SX}
                    iconSx={previewContactIconSx}
                />
            )}

            {phoneNumber && (
                <PreviewContact
                    href={`tel:${phoneNumber}`}
                    icon={phoneIcon}
                    hoverTitle={phoneNumber}
                    clipboardContent={phoneNumber}
                    containerSx={PREVIEW_CONTACT_CONTAINER_SX}
                    iconSx={previewContactIconSx}
                />
            )}
        </>
    );

    if (!trimmedOrganizationName) {
        return previewContacts;
    }

    return (
        <H gap={0.5} sx={{ width: 'unset', minWidth: 0, color: 'text.secondary', fontSize: SIZES.small }}>
            {previewContacts}
            {hasPreviewContact && (
                <Typography variant="body2" color="text.secondary" aria-hidden>
                    |
                </Typography>
            )}
            <Typography variant="body2" noWrap title={trimmedOrganizationName} sx={PREVIEW_CONTACT_INFO_SX}>
                {trimmedOrganizationName}
            </Typography>
        </H>
    );
}
